import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useSettingsStore } from './settingsStore'
import { useSubscriptionStore } from './subscriptionStore'
import { useBoothEventStore } from './boothEventStore'
import { useCreditsStore } from './creditsStore'
import { useFeatureFlagsStore } from './featureFlagsStore'
import {
  capabilities,
  clearAuthTokens as bridgeClearAuthTokens,
  clearSubscriptionState as bridgeClearSubscriptionState,
  getPreloadApi,
  hasElectronPreload,
  isNativeShell,
  startGoogleOAuth as bridgeStartGoogleOAuth,
  syncAuthTokens as bridgeSyncAuthTokens
} from '@renderer/lib/runtime'
import { runGoogleOAuthHandoff } from '@renderer/lib/auth/googleOAuthHandoff'

/**
 * Reset the subscription/credit/feature-flag UI to a signed-out state WITHOUT erasing
 * the device activation code. Used on logout and session-expiry so the same account
 * keeps its device licence and doesn't have to re-enter the token.
 */
const resetSubscriptionUiKeepLicense = (): void => {
  try {
    useSubscriptionStore.getState().clearSubscription()
    useCreditsStore.setState({ credits: 0, package: 'free', error: null })
    // Feature flags are account-scoped — clear them, but keep the device licence.
    useFeatureFlagsStore.getState().clearFlags()
  } catch (error) {
    console.warn('[AUTH] Failed to reset subscription UI:', error)
  }
}

/**
 * Fully clear account-scoped data INCLUDING the activation code. Only called on a
 * genuine account switch (a different account signs in on this device), so account B
 * never inherits account A's device licence.
 */
const clearAccountSubscriptionData = (reason: string): void => {
  try {
    const hadCode = useSettingsStore.getState().activationCode !== ''
    console.warn('[SUBSCRIPTION] Clearing activation code', {
      reason,
      source: 'auth_account_clear',
      hadCode
    })
    // Record the clear on the dashboard while the licence context still exists.
    if (hadCode) {
      useBoothEventStore.getState().recordLicenseIncident('cleared_account_switch', { reason })
    }
    useSettingsStore.getState().setActivationCode('')
    useSettingsStore.getState().setLicenseOwnerEmail(undefined)
    useSubscriptionStore.getState().clearSubscription()
    useCreditsStore.setState({ credits: 0, package: 'free', error: null })
    // Don't let the previous account's feature flags leak into the next login.
    useFeatureFlagsStore.getState().clearFlags()
  } catch (error) {
    console.warn('[AUTH] Failed to clear account subscription data:', error)
  }
}

/**
 * On login, clear the stored activation code ONLY when a different account signs in
 * than the one that owns it (owner is server-confirmed on validate). The same account
 * re-logging in keeps its device licence. When the owner is unknown (e.g. a token that
 * predates owner-tracking), we keep the token — the server still rejects a wrong-account
 * token on validate, so nothing is granted.
 */
const handleAccountSwitchOnLogin = (email: string | undefined): void => {
  try {
    if (!email) return
    const { activationCode, licenseOwnerEmail } = useSettingsStore.getState()
    const hasCode = !!activationCode
    const owner = licenseOwnerEmail?.trim().toLowerCase()
    const next = email.trim().toLowerCase()
    if (hasCode && owner && owner !== next) {
      clearAccountSubscriptionData('account_switch')
    }
  } catch (error) {
    console.warn('[AUTH] account-switch check failed:', error)
  }
}

/**
 * Exchange a session token for the httpOnly cookie the webview sends on every
 * later request. Electron does this through `initializeCookie`; shells without
 * a main process have nowhere else to put the token.
 */
const plantSessionCookie = async (token: string): Promise<void> => {
  const response = await fetch(`${process.env.VITE_API_URL}/api/auth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token })
  })
  if (!response.ok) {
    throw new Error('Signed in with Google, but the session could not be stored on this device')
  }
}

const syncTokensToMainProcess = async (): Promise<void> => {
  try {
    await bridgeSyncAuthTokens()
  } catch (error) {
    console.warn('[AUTH] Failed to sync auth tokens to main process:', error)
  }
}

const clearTokensInMainProcess = async (): Promise<void> => {
  try {
    await bridgeClearAuthTokens()
  } catch (error) {
    console.warn('[AUTH] Failed to clear auth tokens in main process:', error)
  }
}

interface User {
  email: string
  name: string
  cookie?: string
}

export interface GoogleSignInOptions {
  /** Fired once the system browser is open, so the page can explain the wait. */
  onWaiting?: () => void
  /** Abort the wait — used by the cancel button on the waiting panel. */
  signal?: AbortSignal
}

interface AuthState {
  user: User | null
  setUser: (user: User | null) => void
  isAuthenticated: boolean
  login: (
    email: string,
    password: string
  ) => Promise<{ mfaRequired?: boolean; email?: string; tempSessionId?: string }>
  verifyMfa: (email: string, code: string, isBackupCode?: boolean) => Promise<void>
  loginWithGoogle: (mode?: 'login' | 'register', options?: GoogleSignInOptions) => Promise<void>
  logout: () => Promise<void>
  initializeCookie: () => Promise<boolean>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      setUser: (user: User | null): void => void set({ user, isAuthenticated: !!user }),
      login: async (
        email: string,
        password: string
      ): Promise<{ mfaRequired?: boolean; email?: string; tempSessionId?: string }> => {
        const response = await fetch(`${process.env.VITE_API_URL}/api/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ email, password })
        })

        if (!response.ok) {
          let errorMessage = 'Invalid credentials'
          try {
            const errorData = await response.json()
            errorMessage = errorData.error || errorData.message || errorMessage
          } catch {
            errorMessage = response.statusText || errorMessage
          }
          throw new Error(errorMessage)
        }

        const data = await response.json()

        // Check if MFA is required - return MFA info instead of throwing
        if (data.mfaRequired) {
          return {
            mfaRequired: true,
            email: data.email || email,
            tempSessionId: data.tempSessionId
          }
        }

        // Validate response structure
        if (!data || !data.user) {
          throw new Error('Invalid login response: missing user data')
        }

        if (!data.user.email) {
          throw new Error('Invalid login response: missing user email')
        }

        // Clear a previous account's device licence only on a real account switch.
        handleAccountSwitchOnLogin(data.user.email)

        const token = response.headers.get('X-Auth-Token')

        // Store the token in electron mode
        if (hasElectronPreload() && token) {
          const user: User = {
            email: data.user.email,
            name: data.user.name || data.user.email,
            cookie: token
          }
          set({ user, isAuthenticated: true })
          // Add small delay to ensure Zustand persistence completes before syncing
          try {
            await new Promise((resolve) => setTimeout(resolve, 50))
            await syncTokensToMainProcess()
          } catch (error) {
            console.warn('[AUTH] Failed to sync tokens, but login succeeded:', error)
            // Don't throw - login should still succeed
          }
        } else {
          const user: User = {
            email: data.user.email,
            name: data.user.name || data.user.email
          }
          set({ user, isAuthenticated: true })
        }

        return { mfaRequired: false }
      },
      verifyMfa: async (
        email: string,
        code: string,
        isBackupCode: boolean = false
      ): Promise<void> => {
        const response = await fetch(`${process.env.VITE_API_URL}/api/auth/mfa/verify-login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            email,
            code,
            isBackupCode
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Invalid verification code')
        }

        const data = await response.json()
        const token = response.headers.get('X-Auth-Token')

        // Validate response structure
        if (!data || !data.user) {
          throw new Error('Invalid MFA verification response: missing user data')
        }

        if (!data.user.email) {
          throw new Error('Invalid MFA verification response: missing user email')
        }

        // Clear a previous account's device licence only on a real account switch.
        handleAccountSwitchOnLogin(data.user.email)

        // Store the token in electron mode
        if (hasElectronPreload() && token) {
          const user: User = {
            email: data.user.email,
            name: data.user.name || data.user.email,
            cookie: token
          }
          set({ user, isAuthenticated: true })
          // Add small delay to ensure Zustand persistence completes before syncing
          try {
            await new Promise((resolve) => setTimeout(resolve, 50))
            await syncTokensToMainProcess()
            await get().initializeCookie()
          } catch (error) {
            console.warn('[AUTH] Failed to sync tokens, but login succeeded:', error)
            // Don't throw - login should still succeed
          }
        } else {
          const user: User = {
            email: data.user.email,
            name: data.user.name || data.user.email
          }
          set({ user, isAuthenticated: true })
        }
      },
      loginWithGoogle: async (
        mode: 'login' | 'register' = 'login',
        options: GoogleSignInOptions = {}
      ): Promise<void> => {
        // Electron keeps its native bridge untouched: the main process owns
        // token storage there and every live booth runs on it.
        if (!getPreloadApi()?.startGoogleOAuth) {
          if (!capabilities.googleOAuth) {
            throw new Error('Google sign-in is not available on this device')
          }

          // Tauri/Android: same server flow, driven from the renderer. The
          // webview's own cookie is the only thing authenticating later
          // requests here, so plant it BEFORE reporting success — a user
          // marked signed-in without a cookie just 401s on the next screen.
          const { token, user: googleUser } = await runGoogleOAuthHandoff(mode, options)
          await plantSessionCookie(token)
          handleAccountSwitchOnLogin(googleUser.email)
          set({
            user: { email: googleUser.email, name: googleUser.name, cookie: token },
            isAuthenticated: true
          })
          return
        }

        try {
          const result = await bridgeStartGoogleOAuth(mode)

          if (!result.success) {
            throw new Error(result.error || 'OAuth authentication failed')
          }

          if (result.token && result.user) {
            // Clear a previous account's device licence only on a real account switch.
            handleAccountSwitchOnLogin(result.user.email)
            const user: User = {
              email: result.user.email,
              name: result.user.name,
              cookie: result.token
            }
            set({ user, isAuthenticated: true })
            await syncTokensToMainProcess()
          } else {
            throw new Error('OAuth succeeded but no user data received')
          }
        } catch (error) {
          throw error instanceof Error ? error : new Error('OAuth authentication failed')
        }
      },
      logout: async (): Promise<void> => {
        try {
          const response = await fetch(`${process.env.VITE_API_URL}/api/auth`, {
            method: 'DELETE',
            credentials: 'include'
          })

          if (!response.ok) {
            console.error('Failed to logout properly')
          }
        } catch (error) {
          console.error('Error during logout:', error)
        } finally {
          await clearTokensInMainProcess()
          await bridgeClearSubscriptionState()
          // Keep the device activation code on logout — only an account switch clears it.
          resetSubscriptionUiKeepLicense()
          set({ user: null, isAuthenticated: false })
        }
      },
      initializeCookie: async (): Promise<boolean> => {
        const state = get()
        const token = state.user?.cookie

        // Any native shell, not just Electron: a Tauri build now stores a token
        // too (Google handoff), and replanting the cookie on start is what keeps
        // that session alive across app restarts.
        if (isNativeShell() && token) {
          try {
            const response = await fetch(`${process.env.VITE_API_URL}/api/auth/token`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              credentials: 'include',
              body: JSON.stringify({ token })
            })

            if (!response.ok) {
              const isServerError = response.status >= 500
              const isAuthError = response.status === 401 || response.status === 403

              // For server errors, keep cached user (might be temporary)
              if (isServerError) {
                console.warn('[AUTH] Server error during cookie init, keeping cached user')
                return true // Don't block downstream — server might recover
              }

              // For auth errors, clear auth state so downstream code won't fire
              if (isAuthError) {
                console.error('[AUTH] Token is invalid/expired (401/403) — clearing auth state')
                await clearTokensInMainProcess()
                await bridgeClearSubscriptionState()
                // Session expiry is not an account switch — keep the device licence.
                resetSubscriptionUiKeepLicense()
                set({ user: null, isAuthenticated: false })
                return false
              }

              // For other errors, log but don't clear user
              console.error('Failed to initialize cookie - unknown error')
              return false
            } else {
              await syncTokensToMainProcess()
              return true
            }
          } catch (error) {
            console.error('Error initializing cookie:', error)
            // Don't clear user on network error - keep cached user for offline access
            console.log('Keeping cached user for offline access')
            return true // Network error — don't block offline usage
          }
        }
        return !!get().user // Return true if there's a cached user
      }
    }),
    {
      name: 'auth-storage'
    }
  )
)
